import { subDays, subMonths, subWeeks } from 'date-fns';
import { z } from 'zod';

export const WORKFLOW_RELATIVE_DATE_OPERATORS = {
  inTheLast: 'inTheLast',
  olderThan: 'olderThan',
} as const;

export type WorkflowRelativeDateOperator =
  (typeof WORKFLOW_RELATIVE_DATE_OPERATORS)[keyof typeof WORKFLOW_RELATIVE_DATE_OPERATORS];

export const WORKFLOW_RELATIVE_DATE_UNITS = {
  day: 'day',
  month: 'month',
  week: 'week',
} as const;

export type WorkflowRelativeDateUnit =
  (typeof WORKFLOW_RELATIVE_DATE_UNITS)[keyof typeof WORKFLOW_RELATIVE_DATE_UNITS];

export const WorkflowRelativeDateValueSchema = z.object({
  amount: z.number().int().positive(),
  unit: z.enum(WORKFLOW_RELATIVE_DATE_UNITS),
});

export type WorkflowRelativeDateValue = z.infer<typeof WorkflowRelativeDateValueSchema>;

export function getIsWorkflowRelativeDateValue(value: unknown): value is WorkflowRelativeDateValue {
  return WorkflowRelativeDateValueSchema.safeParse(value).success;
}

export function getIsWorkflowRelativeDateOperator(
  value: string
): value is WorkflowRelativeDateOperator {
  return Object.values(WORKFLOW_RELATIVE_DATE_OPERATORS).some(operator => operator === value);
}

export function getRelativeDateCutoff(value: WorkflowRelativeDateValue, now: Date): Date {
  if (value.unit === WORKFLOW_RELATIVE_DATE_UNITS.day) return subDays(now, value.amount);
  if (value.unit === WORKFLOW_RELATIVE_DATE_UNITS.week) return subWeeks(now, value.amount);
  return subMonths(now, value.amount);
}

export function matchesRelativeDate(options: {
  date: Date | null;
  now: Date;
  operator: WorkflowRelativeDateOperator;
  value: WorkflowRelativeDateValue;
}): boolean {
  const { date, now, operator, value } = options;
  if (!date) return false;

  const cutoff = getRelativeDateCutoff(value, now);
  if (operator === WORKFLOW_RELATIVE_DATE_OPERATORS.inTheLast)
    return date.getTime() >= cutoff.getTime() && date.getTime() <= now.getTime();

  return date.getTime() < cutoff.getTime();
}
